import { useState, useEffect, useRef } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  Form,
  Button,
  Spinner,
  ProgressBar,
  Nav,
  Dropdown,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import VideoPlayer from "@/components/VideoPlayer";
import { clientVideosApi } from "@/api";
import Table from "@/components/Elements/Table";
import classNames from "classnames";
import { PIPELINE_OPTIONS } from "@/constants";
import { TEAM_COLORS } from "@/constants/teams";
import LiveTab from "./LiveTab";

const Videos = () => {
  const [activeTab, setActiveTab] = useState("videos");

  const columns = [
    // { accessorKey: "id", header: "Id" },
    {
      accessorKey: "order",
      header: "Order Id",
    },
    {
      accessorKey: "order_type",
      header: "Order Type",
    },
    {
      accessorKey: "created",
      header: "Created",
      cell: (info) =>
        new Date(info.getValue()).toLocaleString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true, // Use 12-hour clock (true) or 24-hour clock (false)
        }),
    },
    {
      accessorKey: "origin",
      header: "Link",
      cell: (props) => (
        <a href={props.getValue()} rel="noreferrer" target="_blank">
          link
        </a>
      ),
    },
    {
      header: "VIDEO LOADED",
      cell: (props) => {
        const meta = props.table.options.meta;
        const isVideoLoaded = props.row.original.is_loaded;
        const homeTeamColor = props.row.original.home_team_color;
        const guestTeamColor = props.row.original.guest_team_color;

        // If video is not loaded, show "No"
        if (!isVideoLoaded) {
          return <div className="tw-text-center">No</div>;
        }

        // If either team color is null, show "No order team colors"
        if (homeTeamColor == null || guestTeamColor == null) {
          return (
            <div className="tw-text-center tw-text-red-500">
              No order team colors
            </div>
          );
        }

        // If video is loaded and both colors are present, show Select button
        return (
          <Button
            size="sm"
            onClick={() => {
              meta.setSelectedVideo(props.row.original);
              // Force re-render of the right panel
              meta.setSelectedVideo(null);
              setTimeout(() => {
                meta.setSelectedVideo(props.row.original);
              }, 0);
            }}
          >
            Select
          </Button>
        );
      },
    },
    {
      header: "Actions",
      cell: (props) => {
        const isVideoLoaded = props.row.original.is_loaded;
        const isLoading = props.row.original.is_loading;
        const orderId = props.row.original.id;
        const orderType = props.row.original.order_type;
        const clientVideoId = props.row.original.id;
        const isReloading = reloadingVideoId === clientVideoId;

        const handleUploadButtonClick = (id) => {
          setIsUploadButtonClicked(true);
          setVideoId(orderId);
        };

        const handleReload = async () => {
          try {
            setReloadingVideoId(clientVideoId);
            await clientVideosApi.reloadVideo(clientVideoId);
            toast.success("Video reload initiated successfully");
            fetchVideos();
          } catch (err) {
            const errorMessage =
              err.response?.data?.message ||
              JSON.stringify(err.response?.data)?.slice(0, 200) ||
              "Something went wrong while reloading video.";
            toast.error(errorMessage);
          } finally {
            setReloadingVideoId(null);
          }
        };

        // Don't show actions for match_analytics_pro
        if (orderType === "match_analytics_pro") {
          return null;
        }

        // Show spinner if video is loading
        if (isLoading) {
          return (
            <div className="tw-flex tw-justify-center">
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            </div>
          );
        }

        return (
          <Dropdown drop="start">
            <Dropdown.Toggle
              id={`actions-dropdown-${clientVideoId}`}
              variant="secondary"
              size="sm"
              disabled={isReloading}
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
            <Dropdown.Item
              onClick={() => handleUploadButtonClick(orderId)}
              disabled={isReloading}
            >
              <FontAwesomeIcon icon={["fas", "upload"]} />{" "}
              {isVideoLoaded ? "Replace" : "Upload"}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={handleReload}
              disabled={isReloading}
            >
              {isReloading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Reloading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={["fas", "redo"]} /> Reload
                </>
              )}
            </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const videoRef = useRef();
  const [videos, setVideos] = useState([]);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [method, setMethod] = useState("file");
  const [isGettingVideo, setIsGettingVideo] = useState(false);
  const [url, setUrl] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [matchTime, setMatchTime] = useState("");
  const [frameTime, setFrameTime] = useState(1);
  const [pipelinePassing, setPipelinePassing] = useState("broadcasting");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isUploadButtonClicked, setIsUploadButtonClicked] = useState(false);
  const [videoId, setVideoId] = useState(NaN);
  const [uploadId, setUploadId] = useState(null);
  const [uploadParts, setUploadParts] = useState([]);
  const [storagePath, setStoragePath] = useState("");
  const [currentPartNumber, setCurrentPartNumber] = useState(1);
  const [totalParts, setTotalParts] = useState(0);
  const CHUNK_SIZE = 500 * 1024 * 1024; // 500MB chunks
  const [homeTeamColor, setHomeTeamColor] = useState("");
  const [guestTeamColor, setGuestTeamColor] = useState("");
  const [reloadingVideoId, setReloadingVideoId] = useState(null);

  const table = useReactTable({
    data: videos,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      setSelectedVideo,
    },
  });

  async function fetchVideos(currentPage = 1) {
    try {
      const { results, count } = await clientVideosApi.getAll(currentPage);
      if (!results) return;
      setTotalCount(count);
      setVideos(currentPage > 1 ? [...videos, ...results] : results);
    } catch (err) {
      toast.error("Something went wrong while fetching videos");
    }
  }

  useEffect(() => {
    fetchVideos(1);
  }, []);

  useEffect(() => {
    if (selectedVideo) {
      setPipelinePassing(selectedVideo.order_type);
      const initialMatchTime =
        selectedVideo.match_time ??
        selectedVideo.match_data?.match_time ??
        "";
      setMatchTime(
        typeof initialMatchTime !== "undefined" && initialMatchTime !== null
          ? initialMatchTime
          : ""
      );
    } else {
      setMatchTime("");
    }
  }, [selectedVideo]);

  const handleFileSelected = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const upload = async (_videoId) => {
    try {
      if (!files.length) {
        toast.warning("Please select a file to upload");
        return;
      }

      const file = files[0];

      // Check if file is large enough for multipart upload (>10MB)
      if (file.size > CHUNK_SIZE) {
        await uploadMultipart(_videoId, file);
      } else {
        await uploadSinglePart(_videoId, file);
      }
    } catch (err) {
      setIsUploading(false);
      toast.error("Error uploading file: " + (err.message || err));
    }
  };

  const uploadSinglePart = async (_videoId, file) => {
    try {
      const data = await clientVideosApi.getS3UploadUrl(_videoId);
      if (!data) {
        toast.error("Couldn't fetch S3 upload url");
        return;
      }
      const { url, fields } = data;

      setIsUploading(true);
      var fd = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        fd.append(key, value);
      });
      fd.append("file", file);

      var xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);

      xhr.addEventListener("load", async function (event) {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            await clientVideosApi.markVideoAsLoaded(_videoId);
            toast.success("Upload Success!");
            fetchVideos();
            setIsUploading(false);
            setIsUploadButtonClicked(false);
          } catch (err) {
            toast.error("Error marking video as loaded");
            setIsUploading(false);
          }
        } else {
          toast.error("Upload failed");
          setIsUploading(false);
        }
      });

      xhr.upload.addEventListener("progress", function (progressEvent) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setProgress(percentCompleted);
      });

      xhr.send(fd);
    } catch (err) {
      throw err;
    }
  };

  const uploadMultipart = async (_videoId, file) => {
    try {
      setIsUploading(true);

      // Step 1: Initialize multipart upload
      const initResponse = await clientVideosApi.initMultipartUpload(_videoId);
      const { upload_id, storage_file } = initResponse;

      // Store these values in local variables instead of relying on state
      const uploadIdLocal = upload_id;
      const storagePathLocal = storage_file;

      // Set state for UI updates
      setUploadId(uploadIdLocal);
      setStoragePath(storagePathLocal);

      // Calculate total parts
      const parts = Math.ceil(file.size / CHUNK_SIZE);
      setTotalParts(parts);

      // Use a local array to track parts instead of state
      const uploadedParts = [];

      // Step 2: Upload each part
      for (let partNumber = 1; partNumber <= parts; partNumber++) {
        setCurrentPartNumber(partNumber);

        // Get the chunk of the file
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(partNumber * CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        // Get presigned URL for this part
        const partUrlResponse = await clientVideosApi.getMultipartUploadUrl(
          storagePathLocal,
          uploadIdLocal,
          partNumber
        );

        // Upload the part to S3
        const etag = await uploadPart(
          partUrlResponse.url,
          chunk,
          partNumber,
          parts
        );

        // Track the uploaded part in local array
        uploadedParts.push({ PartNumber: partNumber, ETag: etag });

        // Update state for UI (not used for logic)
        setUploadParts([...uploadedParts]);
      }

      // Step 3: Complete the multipart upload using local variables
      await clientVideosApi.completeMultipartUpload(
        storagePathLocal,
        uploadIdLocal,
        uploadedParts
      );

      // Step 4: Mark video as loaded
      await clientVideosApi.markVideoAsLoaded(_videoId);

      toast.success("Multipart upload completed successfully!");
      fetchVideos();
      setIsUploading(false);
      setIsUploadButtonClicked(false);
    } catch (err) {
      // Attempt to abort the multipart upload if it was initialized
      // Use the local variables here too
      const uploadIdLocal = uploadId; // Get current state value
      const storagePathLocal = storagePath; // Get current state value

      if (uploadIdLocal && storagePathLocal) {
        try {
          await clientVideosApi.abortMultipartUpload(
            storagePathLocal,
            uploadIdLocal
          );
        } catch (abortErr) {
          console.error("Failed to abort multipart upload:", abortErr);
        }
      }

      throw err;
    }
  };

  const uploadPart = (presignedUrl, chunk, partNumber, totalParts) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl, true);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Extract ETag from response headers
          const etag = xhr.getResponseHeader("ETag").replace(/"/g, "");
          resolve(etag);
        } else {
          reject(
            new Error(`Failed to upload part ${partNumber}: ${xhr.statusText}`)
          );
        }
      };

      xhr.onerror = () => {
        reject(new Error(`Network error uploading part ${partNumber}`));
      };

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          // Calculate overall progress considering all parts
          const partProgress = (event.loaded / event.total) * 100;
          const overallProgress =
            ((partNumber - 1) * 100 + partProgress) / totalParts;
          setProgress(Math.round(overallProgress));
        }
      };

      xhr.send(chunk);
    });
  };

  const getVideoFromUrl = async () => {
    try {
      setIsGettingVideo(true);
      await clientVideosApi.createVideoWithUrl(url, videoId);
      toast.success("Success");
      fetchVideos();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong.";
      toast.error(errorMessage);
    } finally {
      setIsGettingVideo(false);
    }
  };

  const onChangeMethod = (method) => {
    setMethod(method);
  };

  const cutAndCreate = async () => {
    try {
      if (!selectedVideo) return;
      if (!startTime || !endTime) {
        toast.warning("Please input start and end time correctly.");
        return;
      }
      const numericMatchTime = Number(matchTime);
      if (!numericMatchTime || numericMatchTime <= 0) {
        toast.warning("Please enter a valid match time.");
        return;
      }
      if (!homeTeamColor || !guestTeamColor) {
        toast.warning("Please select both team colors.");
        return;
      }
      await clientVideosApi.cutAndCreate(
        selectedVideo.id,
        startTime,
        endTime,
        pipelinePassing,
        homeTeamColor?.toLowerCase(),
        guestTeamColor?.toLowerCase(),
        numericMatchTime
      );
      toast.success("Success!");
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const onVideoLoadHandler = () => {
    const frameTime =
      videoRef.current.webkitDecodedFrameCount / videoRef.current.duration;
    setFrameTime(frameTime);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVideos(nextPage);
  };

  const handleCancelButtonClick = () => {
    setIsUploadButtonClicked(false);
  };

  const setCurrentTimeAsStart = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const formattedTime = formatTime(currentTime);
      setStartTime(formattedTime);
    }
  };

  const setCurrentTimeAsEnd = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const formattedTime = formatTime(currentTime);
      setEndTime(formattedTime);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="tw-w-full tw-h-full tw-flex tw-flex-col">
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={setActiveTab}
        className="tw-px-4 tw-pt-2"
      >
        <Nav.Item>
          <Nav.Link eventKey="videos">Videos</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="live">Live</Nav.Link>
        </Nav.Item>
      </Nav>
      {activeTab === "live" && <LiveTab />}
      <div className={classNames("tw-flex-1 tw-overflow-auto tw-py-3 tw-px-4", activeTab !== "videos" && "tw-hidden")}>
        <div className="tw-h-full tw-flex tw-gap-2 tw-flex-col md:tw-flex-row">
          <div className="tw-flex-1 d-flex flex-column gap-3 h-100">
            <div
              className={classNames(
                isUploadButtonClicked ? "d-flex flex-column gap-2" : "tw-hidden"
              )}
            >
              <Nav
                justify
                variant="tabs"
                defaultActiveKey="file"
                onSelect={onChangeMethod}
              >
                <Nav.Item>
                  <Nav.Link eventKey="file">Upload File</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="url">From URL</Nav.Link>
                </Nav.Item>
              </Nav>
              {method === "file" ? (
                <>
                  <Form.Group controlId="formFile">
                    <Form.Label>Select a file to upload</Form.Label>
                    <Form.Control type="file" onChange={handleFileSelected} />
                  </Form.Group>
                  {progress > 0 && progress < 100 && (
                    <div>
                      <ProgressBar now={progress} />
                      {totalParts > 0 && (
                        <div className="tw-text-center tw-mt-1">
                          Uploading part {currentPartNumber} of {totalParts}
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    className="w-100"
                    variant="success"
                    disabled={isUploading}
                    onClick={() => {
                      upload(videoId);
                    }}
                  >
                    {isUploading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        Uploading
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={["fas", "upload"]} /> Upload File
                      </>
                    )}
                  </Button>
                  <Button
                    className={classNames(
                      "tw-w-full",
                      !isUploadButtonClicked && "tw-hidden"
                    )}
                    variant="danger"
                    onClick={() => {
                      handleCancelButtonClick();
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Form.Group>
                    <Form.Label>URL</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Example: https://disk.yandex.by/i/M2IAIOzRo_fE0g"
                      onChange={(e) => setUrl(e.target.value)}
                    />
                  </Form.Group>
                  <Button
                    className="w-100"
                    variant="success"
                    disabled={isGettingVideo}
                    onClick={getVideoFromUrl}
                  >
                    {isGettingVideo ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        Getting Video
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={["fas", "download"]} /> Get Video
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
            <div className="flex-grow-1 overflow-auto">
              <Table table={table} />
              {videos.length < totalCount && (
                <div className="tw-w-full tw-flex tw-justify-center tw-mt-3">
                  <div className="tw-max-w-[400px] tw-w-full">
                    <Button className="tw-w-full" onClick={loadMore}>
                      Load more
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="tw-flex-1">
            {selectedVideo && (
              <div className="tw-flex tw-flex-col tw-gap-2">
                <VideoPlayer
                  src={selectedVideo.origin}
                  ref={videoRef}
                  fps={1 / frameTime}
                  videoProps={{
                    autoPlay: false,
                    loop: false,
                    muted: false,
                  }}
                  onVideoLoadHandler={onVideoLoadHandler}
                />
                <div className="tw-w-full tw-flex tw-justify-center">
                  <Form.Select
                    value={pipelinePassing}
                    onChange={(e) => setPipelinePassing(e.target.value)}
                    className="tw-w-auto"
                  >
                    {PIPELINE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="tw-flex tw-justify-center tw-gap-4">
                  <Form.Group>
                    <div className="tw-flex tw-justify-between">
                      <Form.Label>Start Time</Form.Label>
                      <Button
                        className="tw-text-xs tw-h-6 tw-rounded-md tw-flex gap-1"
                        variant="success"
                        size="sm"
                        onClick={setCurrentTimeAsStart}
                      >
                        SET TIME
                        <FontAwesomeIcon icon={["fas", "arrow-down"]} />
                      </Button>
                    </div>
                    <Form.Control
                      type="text"
                      placeholder="00:00:00"
                      value={startTime || ""}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <div className="tw-flex tw-justify-between">
                      <Form.Label>End Time</Form.Label>
                      <Button
                        className="tw-text-xs tw-h-6 tw-rounded-md tw-flex gap-1"
                        variant="success"
                        size="sm"
                        onClick={setCurrentTimeAsEnd}
                      >
                        SET TIME
                        <FontAwesomeIcon icon={["fas", "arrow-down"]} />
                      </Button>
                    </div>
                    <Form.Control
                      type="text"
                      placeholder="00:00:00"
                      value={endTime || ""}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Match time</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      placeholder="Enter match time"
                      value={matchTime ?? ""}
                      onChange={(e) => setMatchTime(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Left side jersey color</Form.Label>
                    <Form.Select
                      value={homeTeamColor}
                      onChange={(e) => setHomeTeamColor(e.target.value)}
                    >
                      <option value="">Select color</option>
                      {[
                        selectedVideo?.home_team_color,
                        selectedVideo?.guest_team_color,
                      ]
                        .filter(Boolean)
                        .map((colorValue) => {
                          // Find the color object from TEAM_COLORS for label
                          const colorObj = TEAM_COLORS.find(
                            (c) => c.value === colorValue
                          );
                          return (
                            <option key={colorValue} value={colorValue}>
                              {colorObj ? colorObj.label : colorValue}
                            </option>
                          );
                        })}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Right side jersey color</Form.Label>
                    <Form.Select
                      value={guestTeamColor}
                      onChange={(e) => setGuestTeamColor(e.target.value)}
                    >
                      <option value="">Select color</option>
                      {[
                        selectedVideo?.home_team_color,
                        selectedVideo?.guest_team_color,
                      ]
                        .filter(Boolean)
                        .map((colorValue) => {
                          const colorObj = TEAM_COLORS.find(
                            (c) => c.value === colorValue
                          );
                          return (
                            <option key={colorValue} value={colorValue}>
                              {colorObj ? colorObj.label : colorValue}
                            </option>
                          );
                        })}
                    </Form.Select>
                  </Form.Group>
                </div>
                <Button
                  className="tw-w-full"
                  variant="success"
                  onClick={cutAndCreate}
                >
                  <FontAwesomeIcon icon={["fas", "scissors"]} /> Cut and Create
                  New Task
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Videos;

